<script lang="ts">
  /**
   * Lyktan-vy: täckning per härd (S-04-07, omarbetad i S-04-16).
   *
   * Listar alla aktiva härdar med aggregerad täckning. Hearths där backend
   * tillämpat K=5+l=2-suppression visas som "för få walkare" — UI:t visar
   * aldrig en proxy som kan användas för att kringgå tröskeln.
   *
   * Hard-rules som lyfts av denna vy:
   *  - Inga walk-id eller user-id i svar — vyn renderar bara aggregat.
   *  - Cookie-baserad auth — `credentials: 'include'` i klienten.
   *  - Sessionen sparas inte i localStorage/sessionStorage.
   */
  import { onMount } from 'svelte';
  import {
    fetchCoverageSummary,
    formatDistance,
    formatDuration,
    suppressionMessage,
    type CoverageSummary,
    type CoverageSummaryItem,
  } from './coverageSummaryClient';

  type Props = { onUnauthorized?: () => void };
  const { onUnauthorized }: Props = $props();

  let isLoading = $state(true);
  let errorMessage: string | null = $state(null);
  let summary: CoverageSummary | null = $state(null);

  async function load(): Promise<void> {
    isLoading = true;
    errorMessage = null;
    const result = await fetchCoverageSummary();
    if (result.kind === 'ok') {
      summary = result.summary;
    } else {
      summary = null;
      errorMessage = result.message;
      // 401 mappar till "Sessionen har gått ut" — låt App-skalet hantera
      // utloggning så användaren inte sitter kvar i en delvis vy.
      if (result.message.startsWith('Sessionen har gått ut')) {
        onUnauthorized?.();
      }
    }
    isLoading = false;
  }

  function priorityLabel(p: CoverageSummaryItem['priority']): string {
    switch (p) {
      case 'high':
        return 'Hög prioritet';
      case 'medium':
        return 'Medel prioritet';
      case 'low':
        return 'Låg prioritet';
    }
  }

  onMount(() => {
    void load();
  });
</script>

<section class="coverage" aria-label="Täckning per runda">
  <header>
    <div class="title-row">
      <button
        type="button"
        class="icon-btn icon-btn-secondary"
        onclick={load}
        disabled={isLoading}
        aria-label={isLoading ? 'Laddar' : 'Uppdatera lista'}
        title={isLoading ? 'Laddar…' : 'Uppdatera'}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          class:spinning={isLoading}
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      </button>
    </div>
    <p class="lead">
      Aggregerad statistik per runda. Rundor med få promenörer eller få aktiva dagar visas anonymt
      enligt integritetströsklarna. Inga personuppgifter visas.
    </p>
  </header>

  {#if errorMessage}
    <div class="alert error" role="alert">{errorMessage}</div>
  {/if}

  {#if summary && !isLoading}
    {#if summary.invalidItemCount > 0}
      <!-- Robusthets-fix (arkitektur-svep C3): trasiga rader fäller inte
           vyn — giltiga rader renderas och antalet otolkbara visas. -->
      <div class="alert warning" role="status">
        {summary.invalidItemCount === 1
          ? '1 runda kunde inte tolkas och visas inte.'
          : `${summary.invalidItemCount} rundor kunde inte tolkas och visas inte.`}
        Ladda om listan — kvarstår felet behöver Lyktan uppdateras.
      </div>
    {/if}
    {#if summary.items.length === 0}
      {#if summary.invalidItemCount === 0}
        <p class="empty">Inga rundor har skapats ännu.</p>
      {/if}
    {:else}
      <p class="thresholds">
        Anonymisering: minst {summary.kThreshold} promenörer och minst {summary.lThreshold} aktiva dagar
        krävs för att täckningssiffror ska visas per runda.
      </p>
      <ul class="hearth-list">
        {#each summary.items as item (item.hearthId)}
          <li class="hearth-row" class:suppressed={item.suppressed}>
            <div class="row-head">
              <h3 class="hearth-name">{item.name}</h3>
              <span class="priority priority-{item.priority}">{priorityLabel(item.priority)}</span>
            </div>
            {#if item.suppressed}
              <p class="suppression" role="note">
                {suppressionMessage(item.suppressionReason, summary.kThreshold, summary.lThreshold)}
              </p>
            {:else}
              <dl class="metrics">
                <div class="metric">
                  <dt>Unika promenörer</dt>
                  <dd>{item.distinctWalkers ?? 0}</dd>
                </div>
                <div class="metric">
                  <dt title="Antal gånger någon har promenerat denna runda (en promenör kan räknas flera gånger)">
                    Passager
                  </dt>
                  <dd>{item.passageCount ?? 0}</dd>
                </div>
                <div class="metric">
                  <dt>Total täckning</dt>
                  <dd>{formatDistance(item.totalDistanceM ?? 0)}</dd>
                </div>
                <div class="metric">
                  <dt>Total tid</dt>
                  <dd>{formatDuration(item.totalDurationS ?? 0)}</dd>
                </div>
              </dl>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
</section>

<style>
  .coverage {
    display: grid;
    gap: 1rem;
  }
  header {
    display: grid;
    gap: 0.4rem;
    margin-bottom: 1.5rem;
  }
  .eyebrow {
    font-size: 0.85rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0;
  }
  .title-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
  }
  h2 {
    font-size: 1.9rem;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.025em;
    margin: 0;
  }
  .lead {
    margin: 0;
    color: var(--text-dim);
    max-width: 56em;
  }
  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  .btn.secondary:hover:not(:disabled),
  .btn.secondary:focus-visible:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    cursor: pointer;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0.45rem;
    font-family: inherit;
    transition: color 120ms ease, border-color 120ms ease, background 120ms ease;
  }
  .icon-btn svg { width: 100%; height: 100%; display: block; }
  .icon-btn:hover:not(:disabled) { color: var(--text); border-color: var(--text-dim); }
  .icon-btn:focus-visible { outline: 2px solid var(--glow); outline-offset: 2px; }
  .icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .icon-btn-secondary { color: var(--accent); border-color: var(--accent); }
  .icon-btn-secondary:hover:not(:disabled) { background: var(--accent-soft); color: var(--glow); }
  .spinning { animation: icon-spin 900ms linear infinite; }
  @keyframes icon-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .alert {
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1.25rem;
    font-size: 0.92rem;
    border: 1px solid var(--rule);
  }
  .alert.error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: var(--danger);
  }
  .alert.warning {
    color: var(--text);
    background: var(--accent-soft);
    border-color: var(--accent);
  }
  .empty {
    color: var(--text-dim);
    font-style: italic;
    margin: 0 0 1rem;
  }
  .thresholds {
    font-size: 0.85rem;
    color: var(--text-dim);
    margin: 0 0 1rem;
    max-width: 56em;
  }
  .hearth-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 0.75rem;
  }
  .hearth-row {
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.85rem 1rem;
    background: var(--bg);
  }
  .hearth-row.suppressed {
    border-style: dashed;
    opacity: 0.85;
  }
  .row-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
    flex-wrap: wrap;
  }
  .hearth-name {
    margin: 0;
    font-size: 1rem;
    color: var(--text);
    font-weight: 600;
  }
  .priority {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0.2rem 0.55rem;
    border-radius: 4px;
    border: 1px solid currentColor;
    font-weight: 600;
  }
  .priority-high {
    color: var(--glow);
  }
  .priority-medium {
    color: var(--accent);
  }
  .priority-low {
    color: var(--text-dim);
  }
  .suppression {
    margin: 0;
    font-size: 0.9rem;
    color: var(--text);
    background: var(--accent-soft);
    border-left: 3px solid var(--accent);
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
  }
  .metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.6rem 1.25rem;
    margin: 0;
  }
  .metric dt {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--text-dim);
    font-weight: 600;
    margin-bottom: 0.15rem;
  }
  .metric dd {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--text);
  }
</style>
